import "antd/dist/antd.css";
import "../App.css";

import { Link } from "react-router-dom";
import usePageMeta from "../usePageMeta";

function Privacy() {
    const lastUpdated = "19 May 2026";
    usePageMeta({
        title: "Privacy Policy — Planamoto",
        description: "How Planamoto handles your data.",
        url: "https://www.planamoto.com/privacy",
    });

    return (
        <article className="gt-legal-page">
            <div className="gt-card gt-legal-page__card">
                <div className="gt-eyebrow">Legal</div>
                <h1 className="gt-h2 gt-legal-page__title">
                    Privacy policy
                </h1>
                        <div
                            className="gt-body-sm"
                            style={{
                                color: "var(--gt-text-muted)",
                                marginBottom: 16,
                            }}
                        >
                            Last updated {lastUpdated}
                        </div>

                        <p>
                            This policy explains what personal data Planamoto ("we", "us")
                            collects when you use our tour planning service ("the Service"),
                            how we use it, and the rights you have. We are the data
                            controller for the personal data we hold about you.
                        </p>

                        <h3>1. Data we collect</h3>
                        <ul>
                            <li>
                                <strong>Account data:</strong> email address, display name,
                                and an authentication identifier. If you sign in with Google
                                we receive your email address and a unique identifier from
                                Google.
                            </li>
                            <li>
                                <strong>Tour data you create:</strong> tour names, dates,
                                descriptions, itineraries, expenses, checklists, participant names
                                you enter, and notes.
                            </li>
                            <li>
                                <strong>Technical data:</strong> standard server logs (IP
                                address, user agent, request timestamps) used for security
                                and reliability.
                            </li>
                        </ul>
                        <p>
                            We do not knowingly collect special category data (such as
                            health or biometric data). Please do not put this kind of
                            information into your tour notes.
                        </p>

                        <h3>2. How we use your data</h3>
                        <ul>
                            <li>To provide the Service and the features you use.</li>
                            <li>
                                To authenticate you and keep your account and data secure.
                            </li>
                            <li>
                                To communicate with you about the Service (for example,
                                account verification or material changes to these terms).
                            </li>
                            <li>To diagnose problems and prevent abuse.</li>
                        </ul>

                        <h3>3. Legal basis</h3>
                        <p>
                            We process your personal data on the following bases under UK
                            GDPR: performance of the contract between you and us (to provide
                            the Service), our legitimate interests (to keep the Service
                            secure and reliable), and your consent where required (for
                            example, optional AI features).
                        </p>

                        <h3>4. Sharing your data</h3>
                        <p>
                            We do not sell your personal data. We share data with the
                            following categories of service providers, only to the extent
                            needed to run the Service:
                        </p>
                        <ul>
                            <li>
                                <strong>Hosting and infrastructure:</strong> Amazon Web
                                Services (AWS) for compute, storage, and authentication.
                            </li>
                            <li>
                                <strong>Identity providers:</strong> Google, if you choose
                                to sign in with Google.
                            </li>
                            <li>
                                <strong>Third-party data providers:</strong> map, weather,
                                and exchange-rate providers used to display information
                                inside the Service.
                            </li>
                            <li>
                                <strong>AI providers:</strong> if you use optional AI
                                features (for example, location briefs or itinerary
                                enrichment), the relevant tour data is sent to our AI
                                provider for processing. Your data is not used to train
                                their models.
                            </li>
                        </ul>
                        <p>
                            We may also disclose data where required by law or to protect
                            our rights and the safety of users.
                        </p>

                        <h3>5. International transfers</h3>
                        <p>
                            Some of our service providers are located outside the UK and
                            EEA. Where data is transferred internationally, we rely on
                            appropriate safeguards such as Standard Contractual Clauses or
                            equivalent mechanisms.
                        </p>

                        <h3>6. Retention</h3>
                        <p>
                            We keep your account and tour data for as long as your account
                            is active. If you delete your account, we delete your tours,
                            expenses, itineraries, and checklists within 30 days, except
                            where we are required to retain limited records (for example,
                            for legal or accounting reasons). Server logs are retained for a
                            short period for security and operational purposes.
                        </p>

                        <h3>7. Security</h3>
                        <p>
                            We use industry-standard measures including encryption in
                            transit, access controls, and managed authentication to protect
                            your data. No service can guarantee absolute security; you are
                            responsible for keeping your password safe and for the participants
                            you choose to share tours with.
                        </p>

                        <h3>8. Your rights</h3>
                        <p>Under UK GDPR you have the right to:</p>
                        <ul>
                            <li>Access the personal data we hold about you;</li>
                            <li>Ask us to correct inaccurate data;</li>
                            <li>Ask us to delete your data ("right to erasure");</li>
                            <li>Restrict or object to certain processing;</li>
                            <li>Receive a copy of your data in a portable format;</li>
                            <li>
                                Withdraw consent where processing is based on consent.
                            </li>
                        </ul>
                        <p>
                            You can exercise most of these rights from inside the Service
                            (editing your profile or deleting your account). For anything
                            else, contact us at the address below. You also have the right
                            to lodge a complaint with the UK Information Commissioner's
                            Office (ICO) at{" "}
                            <a
                                href="https://ico.org.uk/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                ico.org.uk
                            </a>
                            .
                        </p>

                        <h3>9. Cookies and local storage</h3>
                        <p>
                            We use a small number of cookies and browser storage entries
                            that are strictly necessary to keep you signed in and to
                            remember your preferences inside the Service. We do not use
                            third-party advertising or tracking cookies.
                        </p>

                        <h3>10. Children</h3>
                        <p>
                            Planamoto is not intended for children under 16. If you believe
                            a child has created an account, please contact us and we will
                            remove it.
                        </p>

                        <h3>11. Changes to this policy</h3>
                        <p>
                            We may update this policy from time to time. If we make a
                            material change we will let you know through the Service or by
                            email. The "last updated" date at the top of this page reflects
                            the latest revision.
                        </p>

                        <h3>12. Outbound and affiliate links</h3>
                        <p>
                            Planamoto includes outbound links to third-party booking and
                            shopping sites (for example, accommodation, ferries, insurance,
                            and kit). Some of these are affiliate links — if you book or buy
                            through them, we may receive a small commission at no extra cost
                            to you. These commissions do not influence what we suggest, and
                            we do not share your tour data with the destination site beyond
                            what is in the link you click (for example, a city or product
                            name).
                        </p>

                        <h3>13. Contact</h3>
                        <p>
                            Questions about this policy or your data? Email{" "}
                            <a href="mailto:support@planamoto.com">
                                support@planamoto.com
                            </a>
                            .
                        </p>

                <div className="gt-body-sm" style={{ textAlign: "center", marginTop: 24 }}>
                    <Link to="/login">Back to sign in</Link>
                </div>
            </div>
        </article>
    );
}

export default Privacy;
